// BannerManagement.jsx

import React, { useState, useEffect } from 'react';

import Layout from 'components/ui/Layout';

import {
  useBanners,
  useDeleteBanner,
} from '../../features/banner/hooks/useBanner';

import AddBannerModal from '../../features/banner/components/AddBannerModal';

import EditBannerModal from '../../features/banner/components/EditBannerModal';

import { useUserProfile } from "features/auth/hooks/useUserProfile";

import {
  ImagePlus,
  Trash2,
  Pencil,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';

const BannerManagement = () => {

  const [filters, setFilters] = useState({
    page: 1,
    limit: 10,
    search: '',
    sortBy: 'createdAt',
    sortOrder: 'desc',
  });

  const [searchInput, setSearchInput] =
    useState('');

  const [isAddModalOpen, setIsAddModalOpen] =
    useState(false);

  const [isEditModalOpen, setIsEditModalOpen] =
    useState(false);

  const [selectedBanner, setSelectedBanner] =
    useState(null);

  // IMAGE PREVIEW
  const [selectedImage, setSelectedImage] =
    useState(null);

  // GET BANNERS
  const {
    data,
    isPending,
    isError,
    error,
  } = useBanners(filters);

  // DELETE
  const deleteBannerMutation =
    useDeleteBanner();

  const banners =
    data?.data?.transformedBanner || [];

  const pagination =
    data?.data?.pagination;

  const { data: profileData } = useUserProfile();
  const user = profileData?.data;

  // console.log('Current Role:', user?.role?.name);

  const restrictedRoles = [
    "delivery_manager",
    "category_manager",
    "operations_manager",
    "travel_manager",
    'finance_manager'
  ];

  const showPermissionError = restrictedRoles.includes(user?.role?.name);


  // FILTER UPDATE
  const updateFilter = (key, value) => {

    setFilters((prev) => ({
      ...prev,
      [key]: value,
      page: key === 'page' ? value : 1,
    }));
  };

  // SEARCH
  useEffect(() => {

    const timer = setTimeout(() => {

      if (searchInput !== filters.search) {
        updateFilter('search', searchInput);
      }

    }, 500);

    return () => clearTimeout(timer);

  }, [searchInput]);

  // DELETE
  const handleDeleteBanner = async (id) => {

    const confirmed = window.confirm(
      'Are you sure you want to delete this banner?'
    );

    if (!confirmed) return;

    try {

      await deleteBannerMutation.mutateAsync(id);

      alert('Banner deleted successfully');

    } catch (error) {

      alert(
        error?.response?.data?.message ||
        'Failed to delete banner'
      );
    }
  };

  const handleSort = (column) => {
    const newOrder =
      filters.sortBy === column &&
        filters.sortOrder === 'asc'
        ? 'desc'
        : 'asc';

    setFilters((prev) => ({
      ...prev,
      sortBy: column,
      sortOrder: newOrder,
      page: 1,
    }));
  };

  const getPageNumbers = () => {
    if (!pagination) return [];

    const currentPage = pagination.page;
    const totalPages = pagination.totalPages;
    const pages = [];
    const maxVisible = 5;

    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else if (currentPage <= 3) {
      for (let i = 1; i <= 4; i++) pages.push(i);
      pages.push('...');
      pages.push(totalPages);
    } else if (currentPage >= totalPages - 2) {
      pages.push(1);
      pages.push('...');
      for (let i = totalPages - 3; i <= totalPages; i++) pages.push(i);
    } else {
      pages.push(1);
      pages.push('...');
      for (let i = currentPage - 1; i <= currentPage + 1; i++) pages.push(i);
      pages.push('...');
      pages.push(totalPages);
    }

    return pages;
  };


  const SortIcon = ({ column }) => {
    if (filters.sortBy !== column) {
      return (
        <span className="text-gray-400">
          ↕
        </span>
      );
    }

    return filters.sortOrder === 'asc'
      ? <span>↑</span>
      : <span>↓</span>;
  };

  return (
    <Layout>
      <div className="min-h-screen bg-white text-gray-900 p-6">
        <div className="max-w-7xl mx-auto">
          {/* HEADER */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 space-y-4 sm:space-y-0">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-1">
                Banner Management
              </h3>

              <p className="text-sm text-gray-500">
                Manage application banners
              </p>
            </div>

            {!showPermissionError && (
              <button
                onClick={() => setIsAddModalOpen(true)}
                className="flex items-center space-x-2 px-4 py-2 text-sm bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors"
              >
                <ImagePlus size={16} />
                <span>Add Banner</span>
              </button>
            )}
          </div>

          {/* TABLE */}
          <div className="bg-white border border-gray-200 rounded-lg overflow-hidden shadow-sm">
            <div className="overflow-x-auto">
              <table className="w-full">
                {/* TABLE HEAD */}
                <thead className="bg-gray-100 border-b border-gray-200">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase">
                      Banner
                    </th>

                    {/* <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase">
                      Campaign Name
                    </th> */}

                    <th
                      className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase cursor-pointer"
                      onClick={() => handleSort("campaign_name")}
                    >
                      <div className="flex items-center gap-1">
                        Campaign Name
                        <SortIcon column="campaign_name" />
                      </div>
                    </th>

                    {/* <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase">
                      Screen Name
                    </th> */}

                    <th
                      className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase cursor-pointer"
                      onClick={() => handleSort("screen_name")}
                    >
                      <div className="flex items-center gap-1">
                        Screen Name
                        <SortIcon column="screen_name" />
                      </div>
                    </th>

                    {/* <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase">
                      Created At
                    </th> */}

                    <th
                      className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase cursor-pointer"
                      onClick={() => handleSort("createdAt")}
                    >
                      <div className="flex items-center gap-1">
                        Created At
                        <SortIcon column="createdAt" />
                      </div>
                    </th>

                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-600 uppercase">
                      Actions
                    </th>
                  </tr>
                </thead>

                {/* TABLE BODY */}
                <tbody className="divide-y divide-gray-200">
                  {showPermissionError ? (
                    <tr>
                      <td
                        colSpan="11"
                        className="px-4 py-8 text-center text-destructive"
                      >
                        Error loading CMS: You do not have permission to perform
                        this action!
                      </td>
                    </tr>
                  ) : isPending ? (
                    <tr>
                      <td
                        colSpan={5}
                        className="px-4 py-6 text-center text-gray-500"
                      >
                        Loading...
                      </td>
                    </tr>
                  ) : isError ? (
                    <tr>
                      <td
                        colSpan={5}
                        className="px-4 py-6 text-center text-red-500"
                      >
                        Error: {error?.message}
                      </td>
                    </tr>
                  ) : banners.length === 0 ? (
                    <tr>
                      <td
                        colSpan={5}
                        className="px-4 py-6 text-center text-gray-500"
                      >
                        No banners found.
                      </td>
                    </tr>
                  ) : (
                    banners.map((banner) => (
                      <tr
                        key={banner._id}
                        className="hover:bg-gray-50 transition"
                      >
                        {/* IMAGE */}
                        <td className="px-4 py-3">
                          <img
                            src={banner.banner_img_key}
                            alt="Banner"
                            onClick={() =>
                              setSelectedImage(banner.banner_img_key)
                            }
                            className="w-32 h-20 object-cover rounded-md border cursor-pointer hover:scale-105 transition duration-200"
                          />
                        </td>

                        {/* CAMPAIGN */}
                        <td className="px-4 py-3 text-sm text-gray-800">
                          {banner.campaign_name}
                        </td>

                        {/* SCREEN */}
                        <td className="px-4 py-3 text-sm text-gray-800">
                          {banner.screen_name}
                        </td>

                        {/* CREATED */}
                        <td className="px-4 py-3 text-sm text-gray-800">
                          {new Date(banner.createdAt).toLocaleString()}
                        </td>

                        {/* ACTIONS */}
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-end gap-3">
                            {/* EDIT */}
                            <button
                              className="p-1 hover:bg-muted rounded transition-colors"
                              title="Edit Banner"
                            >
                              <Pencil
                                size={16}
                                className="text-muted-foreground hover:text-foreground"
                                onClick={() => {
                                  setSelectedBanner(banner);

                                  setIsEditModalOpen(true);
                                }}
                              />
                            </button>

                            {/* DELETE */}
                            <button
                              onClick={() => handleDeleteBanner(banner._id)}
                              className="p-1 hover:bg-muted rounded transition-colors"
                              title="Delete Banner"
                            >
                              <Trash2
                                size={16}
                                className="text-muted-foreground hover:text-destructive"
                              />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>


            {/* PAGINATION */}
{pagination && (
  <div className="flex flex-col sm:flex-row items-center justify-between px-4 py-3 border-t border-gray-200 space-y-3 sm:space-y-0">
    <div className="text-sm text-gray-600">
      Showing{' '}
      {(pagination.page - 1) * pagination.limit + 1} to{' '}
      {Math.min(pagination.page * pagination.limit, pagination.total)}{' '}
      of {pagination.total} banners
    </div>

    <div className="flex items-center space-x-2">
      <button
        onClick={() => updateFilter('page', pagination.page - 1)}
        disabled={pagination.page === 1}
        className="p-2 border border-gray-200 rounded-md bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
      >
        <ChevronLeft size={16} />
      </button>

      <div className="flex items-center space-x-1">
        {getPageNumbers().map((pageNum, idx) =>
          pageNum === '...' ? (
            <span key={`ellipsis-${idx}`} className="px-2 text-gray-400">
              ...
            </span>
          ) : (
            <button
              key={pageNum}
              onClick={() => updateFilter('page', pageNum)}
              className={`min-w-[2rem] px-3 py-1 text-sm rounded-md transition-colors ${
                pageNum === pagination.page
                  ? 'bg-primary text-primary-foreground font-medium'
                  : 'bg-white hover:bg-gray-50 border border-gray-200 text-gray-700'
              }`}
            >
              {pageNum}
            </button>
          )
        )}
      </div>

      <button
        onClick={() => updateFilter('page', pagination.page + 1)}
        disabled={pagination.page === pagination.totalPages}
        className="p-2 border border-gray-200 rounded-md bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
      >
        <ChevronRight size={16} />
      </button>
    </div>
  </div>
)}
           
          </div>
        </div>
      </div>

      {/* ADD MODAL */}
      <AddBannerModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
      />

      {/* EDIT MODAL */}
      <EditBannerModal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        banner={selectedBanner}
      />

      {/* IMAGE PREVIEW MODAL */}
      {selectedImage && (
        <div
          className="fixed inset-0 z-[100] bg-black/80 flex items-center justify-center p-4"
          onClick={() => setSelectedImage(null)}
        >
          <div
            className="relative max-w-6xl w-full flex justify-center"
            onClick={(e) => e.stopPropagation()}
          >
            {/* CLOSE BUTTON */}
            <button
              onClick={() => setSelectedImage(null)}
              className="absolute -top-12 right-0 text-white text-4xl hover:text-gray-300"
            >
              ×
            </button>

            {/* LARGE IMAGE */}
            <img
              src={selectedImage}
              alt="Banner Preview"
              className="max-h-[90vh] w-auto object-contain rounded-lg shadow-2xl"
            />
          </div>
        </div>
      )}
    </Layout>
  );
};

export default BannerManagement;